/**
 * Client-side, offline renderers for forms LINKED to a JHA (Fall Arrest Rescue
 * Plan, CSEP, LTT, …). They produce full-page HTML that is appended to the JHA
 * PDF and rendered exactly like the JHA pages (html2canvas → jsPDF), so the
 * linked form shows up pixel-faithfully even offline.
 *
 * Mirrors the server PDF templates (e.g. templates/pdf/safety/
 * fall_arrest_rescue_plan.html.twig) so the in-app preview matches the emailed
 * PDF.
 *
 * EXTENSIBLE: to add a new linked form, write a render function that returns the
 * inner HTML of one portrait page and register it in FORM_RENDERERS by type key.
 * Everything else (page numbering, orientation, styling, pagination) is handled
 * by renderLinkedFormPages + the shared LINKED_FORM_STYLES.
 */

import {
    PRE_ENTRY_QUESTIONS,
    CONFINED_SPACE_TYPES,
    SPECIAL_REQUIREMENTS,
    VERIFICATION_ITEMS,
    AIR_CONTAMINANTS,
} from '../../csep/utils/constants';
import { LOTO_CHECKLIST_ITEMS, ENERGY_TYPES as LTT_ENERGY_TYPES } from '../../ltt/utils/constants';
import { TENANT_PDF } from '../../../config/tenant';

// Company header — mirrors templates/pdf/partials/header.html.twig (logo + name +
// tagline + services line). Rendered at the top of every linked-form page.
function companyHeaderHtml() {
    const t = TENANT_PDF || {};
    return `<table class="lf-hdr" width="100%" cellspacing="0" cellpadding="0"><tr>
        <td width="22%" valign="middle">${t.logoUrl ? `<img src="${t.logoUrl}" alt="logo" class="lf-hdr-logo">` : ''}</td>
        <td width="78%" valign="middle" style="text-align:right;">
            <div class="lf-hdr-name">${esc(t.companyName || '')}</div>
            <div class="lf-hdr-rule"></div>
            ${t.tagline ? `<div class="lf-hdr-tag">${esc(t.tagline)}</div>` : ''}
            ${t.servicesLine ? `<div class="lf-hdr-svc">${t.servicesLine}</div>` : ''}
        </td>
    </tr></table>`;
}

// Company footer — mirrors templates/pdf/partials/footer.html.twig (address + tel/fax).
function companyFooterHtml() {
    const t = TENANT_PDF || {};
    return `<div class="lf-ftr">
        <div class="lf-ftr-rule"></div>
        <div class="lf-ftr-addr">${esc(t.address || '')}</div>
        <div class="lf-ftr-tel">${t.phone ? `TEL ${esc(t.phone)}` : ''}${t.fax ? ` * FAX ${esc(t.fax)}` : ''}</div>
    </div>`;
}

function esc(str) {
    if (str === null || str === undefined) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function fmtDate(value) {
    if (!value) return '';
    // Date-only "YYYY-MM-DD" values are formatted straight from their parts.
    // `new Date("2026-06-08")` parses as UTC midnight and renders as the previous
    // day in any timezone west of UTC (e.g. 06/07 on a US Eastern iPad).
    if (typeof value === 'string') {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
        if (m) return `${m[2]}/${m[3]}/${m[1]}`;
    }
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
}

function fmtDateTime(value) {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()} ${hh}:${mi}`;
}

const checkbox = (checked, label) =>
    `<span class="lf-checkbox-line${checked ? ' checked' : ''}"><span class="lf-box"></span>${label}</span>`;

/**
 * Shared styles for all linked-form pages. Scoped under `.lf-page` so they never
 * clash with the JHA PDF styles. Mirrors templates/pdf/partials/head.html.twig +
 * the FARP-specific block.
 */
export const LINKED_FORM_STYLES = `
.lf-page { font-family: Arial, 'DejaVu Sans', sans-serif; font-size: 7.5pt; line-height: 1.15; color: #000; }
.lf-page * { box-sizing: border-box; }
/* Company header (mirrors pdf/partials/header.html.twig) */
.lf-hdr { margin-bottom: 8px; }
.lf-hdr-logo { width: 170px; height: auto; display: block; }
.lf-hdr-name { font-size: 15pt; font-weight: bold; letter-spacing: 0.5px; line-height: 1; text-align: center; }
.lf-hdr-rule { border-bottom: 2px solid #000; margin: 3px 0 1px 0; }
.lf-hdr-tag { font-size: 13pt; font-style: italic; color: #000; margin-bottom: 3px; text-align: center; }
.lf-hdr-svc { font-size: 9pt; color: #000; line-height: 1.2; text-align: center; }
/* Company footer (mirrors pdf/partials/footer.html.twig) */
.lf-ftr { margin-top: 12px; text-align: center; }
.lf-ftr-rule { border-top: 1px solid #999; margin-bottom: 6px; }
.lf-ftr-addr { font-size: 9pt; color: #333; margin-bottom: 2px; }
.lf-ftr-tel { font-size: 8pt; color: #333; }
.lf-doc-title { text-align: center; font-size: 13pt; font-weight: bold; margin: 2px 0 1px 0; letter-spacing: 0.5px; }
.lf-subtitle { text-align: center; font-size: 8.5pt; color: #555; margin: 0 0 6px 0; }
.lf-section-title { background-color: #1976d2; color: #fff; font-weight: bold; text-transform: uppercase; padding: 2px 6px; font-size: 8pt; margin-top: 5px; margin-bottom: 2px; }
.lf-info-table { width: 100%; border-collapse: collapse; margin-bottom: 3px; }
.lf-info-table td { padding: 2px 5px; border: 1px solid #ccc; vertical-align: top; font-size: 8pt; }
.lf-info-table .label { background-color: #f5f5f5; font-weight: bold; width: 22%; white-space: nowrap; }
.lf-two-col { display: table; width: 100%; border-collapse: collapse; margin-bottom: 2px; }
.lf-two-col > .lf-col { display: table-cell; vertical-align: top; padding-right: 8px; width: 50%; }
.lf-two-col > .lf-col:last-child { padding-right: 0; }
.lf-badge { display: inline-block; padding: 1px 6px; border-radius: 2px; font-size: 7.5pt; font-weight: bold; color: #fff; }
.lf-badge.draft { background-color: #888; }
.lf-badge.submitted { background-color: #2e7d32; }
.lf-text-block { min-height: 13px; white-space: pre-wrap; word-wrap: break-word; }
.lf-checkbox-line { display: block; padding: 1px 0; font-size: 8pt; line-height: 1.25; }
.lf-checkbox-line .lf-box { display: inline-block; width: 9px; height: 9px; border: 1px solid #555; margin-right: 4px; vertical-align: middle; text-align: center; line-height: 8px; font-size: 8pt; }
.lf-checkbox-line.checked .lf-box { background-color: #1976d2; border-color: #1976d2; color: #fff; }
.lf-checkbox-line.checked .lf-box::before { content: '\\2713'; }
.lf-checklist-table { width: 100%; border-collapse: collapse; margin-bottom: 3px; }
.lf-checklist-table td, .lf-checklist-table th { padding: 2px 5px; border: 1px solid #ccc; vertical-align: top; font-size: 7.5pt; line-height: 1.15; }
.lf-checklist-table th { background-color: #f0f0f0; font-weight: bold; text-align: left; }
.lf-answer-yes { color: #2e7d32; font-weight: bold; }
.lf-answer-no { color: #c62828; font-weight: bold; }
.lf-ordered-list { margin: 0; padding-left: 16px; font-size: 8pt; }
.lf-ordered-list li { margin-bottom: 1px; line-height: 1.25; }
.lf-submitter-table { width: 100%; border-collapse: collapse; }
.lf-submitter-table td { padding: 3px 5px; border: 1px solid #ccc; vertical-align: middle; font-size: 8pt; }
.lf-submitter-table .label { background-color: #f5f5f5; font-weight: bold; white-space: nowrap; }
.lf-signature-image { border: 1px solid #ccc; max-width: 200px; max-height: 46px; display: inline-block; vertical-align: middle; }
.lf-signature-meta { font-size: 6.5pt; color: #666; display: inline-block; margin-left: 6px; vertical-align: middle; }
.lf-muted { color: #888; }

/* ─── CSEP-specific (black headers, dense tables) — mirrors confined_space_entry_permit/pdf.html.twig ─── */
.lf-csep .lf-doc-title { font-size: 13pt; margin: 4px 0 4px 0; }
.lf-csep .lf-csep-subtitle { text-align: center; font-size: 8.5pt; color: #444; margin-bottom: 8px; }
.lf-csep .lf-csep-section { background-color: #000; color: #fff; font-weight: bold; text-transform: uppercase; padding: 3px 6px; font-size: 8.5pt; margin-top: 7px; margin-bottom: 4px; }
.lf-csep .lf-csep-info { width: 100%; border-collapse: collapse; margin-bottom: 5px; }
.lf-csep .lf-csep-info td { padding: 3px 4px; font-size: 8pt; vertical-align: top; border: 1px solid #000; }
.lf-csep .lf-csep-info .lbl { font-weight: bold; width: 30%; background: #f3f3f3; }
.lf-csep .lf-two { width: 100%; border-collapse: collapse; }
.lf-csep .lf-two > td { width: 50%; padding: 0 5px; vertical-align: top; }
.lf-csep table.lf-csep-grid { width: 100%; border-collapse: collapse; table-layout: fixed; margin-bottom: 4px; }
.lf-csep table.lf-csep-grid th, .lf-csep table.lf-csep-grid td { border: 1px solid #000; padding: 1px 3px; font-size: 6.5pt; line-height: 1.1; vertical-align: middle; overflow: hidden; }
.lf-csep table.lf-csep-grid th { background-color: #000; color: #fff; font-weight: bold; text-align: center; }
.lf-csep .lf-csep-yes { font-weight: bold; color: #006600; }
.lf-csep .lf-csep-no { font-weight: bold; color: #cc0000; }
.lf-csep .lf-csep-na { color: #666; }
.lf-csep .lf-csep-cbox { display: inline-block; width: 9px; height: 9px; border: 1px solid #000; position: relative; top: 1px; }
.lf-csep .lf-csep-cbox.on { background-color: #000; }
.lf-csep .lf-csep-sigbox { border: 1px solid #333; padding: 5px; margin-bottom: 6px; min-height: 60px; }
.lf-csep .lf-csep-sigbox h6 { margin: 0 0 4px 0; font-size: 9pt; font-weight: bold; }
.lf-csep .lf-csep-sigbox td { padding: 2px 0; font-size: 8pt; }
.lf-csep .lf-csep-sigimg { max-width: 200px; max-height: 50px; margin-top: 4px; }

/* ─── LTT-specific — mirrors lock_tag_try_permit/pdf.html.twig ─── */
.lf-ltt .lf-doc-title { font-size: 14pt; margin: 6px 0; }
.lf-ltt .lf-ltt-section { background-color: #000; color: #fff; font-weight: bold; text-transform: uppercase; padding: 3px 6px; font-size: 8pt; margin-top: 7px; margin-bottom: 0; }
.lf-ltt .lf-ltt-info { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
.lf-ltt .lf-ltt-info td { padding: 3px 4px; font-size: 8pt; vertical-align: top; border: 1px solid #000; }
.lf-ltt .lf-ltt-info .lbl { font-weight: bold; background: #f5f5f5; }
.lf-ltt .lf-two { width: 100%; border-collapse: collapse; }
.lf-ltt .lf-two > td { vertical-align: top; padding: 0 4px; }
.lf-ltt .lf-box2 { border: 1px solid #000; padding: 4px; margin-bottom: 4px; }
.lf-ltt table.lf-ltt-loto { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
.lf-ltt table.lf-ltt-loto th, .lf-ltt table.lf-ltt-loto td { border: 1px solid #000; padding: 2px 4px; font-size: 7pt; vertical-align: middle; }
.lf-ltt table.lf-ltt-loto th { background-color: #000; color: #fff; font-weight: bold; text-align: center; }
.lf-ltt .lf-ltt-proc { background-color: #cc0000; color: #fff; font-weight: bold; text-align: center; }
.lf-ltt .lf-ltt-yes { font-weight: bold; color: #006600; }
.lf-ltt .lf-ltt-no { font-weight: bold; color: #cc0000; }
.lf-ltt .lf-ltt-na { color: #666; }
.lf-ltt .lf-cbox { display: inline-block; width: 9px; height: 9px; border: 1px solid #000; margin-right: 4px; position: relative; top: 1px; }
.lf-ltt .lf-cbox.on { background-color: #000; }
.lf-ltt .lf-ltt-sigbox { border: 1px solid #000; padding: 5px; margin-bottom: 6px; }
.lf-ltt .lf-ltt-sigbox h6 { margin: -5px -5px 4px -5px; font-size: 8pt; font-weight: bold; background: #000; color: #fff; padding: 2px 4px; }
.lf-ltt .lf-ltt-sigbox .desc { font-size: 7pt; font-style: italic; margin: 0 0 4px 0; color: #333; }
.lf-ltt .lf-ltt-sigbox table { width: 100%; border-collapse: collapse; }
.lf-ltt .lf-ltt-sigbox td { padding: 2px 0; font-size: 8pt; }
.lf-ltt .lf-ltt-sigimg { max-width: 90px; max-height: 30px; }
.lf-ltt table.lf-ltt-log { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
.lf-ltt table.lf-ltt-log th, .lf-ltt table.lf-ltt-log td { border: 1px solid #000; padding: 3px 4px; font-size: 7pt; vertical-align: middle; }
.lf-ltt table.lf-ltt-log th { background: #000; color: #fff; font-weight: bold; text-align: center; }
.lf-ltt .lf-dur { width: 100%; border-collapse: collapse; }
.lf-ltt .lf-dur th, .lf-ltt .lf-dur td { border: 1px solid #000; padding: 2px 4px; font-size: 8pt; text-align: center; }
.lf-ltt .lf-dur th { background: #f5f5f5; font-weight: bold; width: 15%; }
`;

const FARP_EQUIPMENT = [
    { key: 'mewps', label: 'MEWPs' },
    { key: 'ladder', label: 'Ladder' },
    { key: 'rescuePositioningDevice', label: 'Rescue Positioning Device (RPD)' },
    { key: 'manualEntry', label: 'Manual Entry', hint: 'Requires Authorization from Safety Professional' },
];
const FARP_LEGACY_EQUIPMENT = {
    rescueLadder: 'Rescue Ladder', extLadder: 'Ext Ladder', rescueStep: 'Rescue Step (self-deployed)',
    rescuePole: 'Rescue Pole', rescueRope: 'Rescue Rope', stokesLitter: 'Stokes Litter',
    blockTackle: 'Block & Tackle', firstAidKit: 'First Aid Kit', lifeRing: 'Life Ring',
    workVest: 'Work Vest', cuttingDevice: 'Cutting Device',
};
const FARP_LEGACY_LOCATION = {
    safetyOffice: 'Safety Office', toolHouse: 'Tool House', gangBox: 'Gang Box', manualEntry: 'Manual Entry',
};

/**
 * Faithful Fall Arrest Rescue Plan — mirrors fall_arrest_rescue_plan.html.twig.
 * Returns the INNER html of one portrait page (without the page wrapper).
 */
function renderFarpInner(plan, opts = {}) {
    const moc = plan.methodOfContact || {};
    const equip = plan.rescueEquipment || {};
    const equipLoc = plan.equipmentLocation || {};
    const rescuers = plan.rescuers || [];
    const competent = plan.competentPerson || {};
    const emergency = plan.emergencyContact || {};
    const checklist = plan.checklist || [];
    const preWorkTasks = plan.preWorkTasks || [];
    const responseProcedure = plan.responseProcedure || [];
    const status = (plan.status || 'draft').toLowerCase();

    const rescuersText = rescuers.length
        ? rescuers.map(r => `${esc(r.name || '—')}${r.source ? ` (${esc(String(r.source).toUpperCase())})` : ''}`).join(', ')
        : '—';

    // Method of contact
    let mocHtml = '';
    mocHtml += checkbox(!!moc.faceToFace, 'Verbal / Face-to-Face');
    {
        const on = moc.radio && moc.radio.enabled;
        const ch = (moc.radio && moc.radio.channels) || [];
        mocHtml += checkbox(!!on, `Radio Channel${on && ch.length ? `: <strong>${esc(ch.join(', '))}</strong>` : ''}`);
    }
    {
        const on = moc.phone && moc.phone.enabled;
        const nums = (moc.phone && moc.phone.numbers) || [];
        mocHtml += checkbox(!!on, `Phone #${on && nums.length ? `: <strong>${esc(nums.join(', '))}</strong>` : ''}`);
    }
    if (moc.pa) mocHtml += checkbox(true, 'PA');
    (moc.custom || []).forEach(m => {
        if (m && m.label) mocHtml += checkbox(true, `${esc(m.label)}${m.value ? `: <strong>${esc(m.value)}</strong>` : ''}`);
    });

    // Rescue equipment
    let equipHtml = '';
    FARP_EQUIPMENT.forEach(item => {
        equipHtml += checkbox(!!equip[item.key], `${esc(item.label)}${item.hint ? ` <span class="lf-muted">(${esc(item.hint)})</span>` : ''}`);
    });
    (equip.customs || []).forEach(eq => {
        const v = typeof eq === 'string' ? eq : (eq && (eq.label || eq.value));
        if (v) equipHtml += checkbox(true, `<span class="lf-muted">Other:</span> ${esc(v)}`);
    });
    Object.entries(FARP_LEGACY_EQUIPMENT).forEach(([k, label]) => {
        if (equip[k]) equipHtml += checkbox(true, esc(label));
    });

    // Equipment location
    let locHtml = '';
    locHtml += checkbox(!!equipLoc.within100, "Within 100'");
    locHtml += checkbox(!!equipLoc.from100to300, "100' - 300'");
    locHtml += checkbox(!!equipLoc.over300, "300' or more");
    (equipLoc.customs || []).forEach(loc => {
        const v = typeof loc === 'string' ? loc : (loc && (loc.label || loc.value));
        if (v) locHtml += checkbox(true, `<span class="lf-muted">Other:</span> ${esc(v)}`);
    });
    Object.entries(FARP_LEGACY_LOCATION).forEach(([k, label]) => {
        if (equipLoc[k]) locHtml += checkbox(true, esc(label));
    });

    // Checklist rows
    const checklistRows = checklist.length
        ? checklist.map(item => {
            const ans = item.answer === 'yes'
                ? '<span class="lf-answer-yes">YES</span>'
                : item.answer === 'no'
                    ? '<span class="lf-answer-no">NO</span>'
                    : '—';
            return `<tr><td>${esc(item.question || '')}</td><td style="text-align:center;">${ans}</td><td><div class="lf-text-block">${esc(item.comment || '')}</div></td></tr>`;
        }).join('')
        : '<tr><td colspan="3" style="text-align:center;" class="lf-muted">No checklist data</td></tr>';

    const preWorkHtml = preWorkTasks.length
        ? `<ol class="lf-ordered-list">${preWorkTasks.map(t => `<li>${esc(t.text || '')}</li>`).join('')}</ol>`
        : '<div class="lf-muted" style="padding:4px;">—</div>';
    const responseHtml = responseProcedure.length
        ? `<ol class="lf-ordered-list">${responseProcedure.map(r => `<li>${esc(r.text || '')}</li>`).join('')}</ol>`
        : '<div class="lf-muted" style="padding:4px;">—</div>';

    const sigCell = plan.submitterSignature
        ? `<img class="lf-signature-image" src="${plan.submitterSignature}" alt="Submitter signature">${plan.submitterSignatureDate ? `<span class="lf-signature-meta">${esc(fmtDateTime(plan.submitterSignatureDate))}</span>` : ''}`
        : '<em class="lf-muted">No signature on file.</em>';

    return `
        <div class="lf-doc-title">FALL ARREST RESCUE PLAN</div>
        <div class="lf-subtitle">
            <span class="lf-badge ${status}">${esc(status.toUpperCase())}</span>
            ${plan.jobCode ? ` &nbsp;|&nbsp; Job <strong>${esc(plan.jobCode)}</strong>` : ''}
            ${plan.planDate ? ` &nbsp;|&nbsp; Date <strong>${esc(fmtDate(plan.planDate))}</strong>` : ''}
        </div>

        <div class="lf-section-title">Job &amp; Date</div>
        <table class="lf-info-table">
            <tr><td class="label">Job #</td><td>${esc(plan.jobCode || '—')}</td><td class="label">Job Name</td><td>${esc(plan.jobName || '—')}</td></tr>
            <tr><td class="label">Customer</td><td>${esc(plan.customerName || '—')}</td><td class="label">Plan Date</td><td>${esc(plan.planDate ? fmtDate(plan.planDate) : '—')}</td></tr>
            <tr><td class="label">Location</td><td colspan="3">${esc(plan.jobLocation || '—')}</td></tr>
            <tr><td class="label">Job Description</td><td colspan="3"><div class="lf-text-block">${esc(plan.jobDescription || '—')}</div></td></tr>
        </table>

        <div class="lf-two-col">
            <div class="lf-col">
                <div class="lf-section-title">Contacts</div>
                <table class="lf-info-table">
                    <tr><td class="label" style="width:38%;">Rescuer(s)</td><td>${rescuersText}</td></tr>
                    <tr><td class="label">Competent Person</td><td>${esc(competent.name || '—')}</td></tr>
                    <tr><td class="label">Emergency Contact</td><td>${esc(emergency.name || '—')}${emergency.phone ? `<br><span style="color:#555;">${esc(emergency.phone)}</span>` : ''}</td></tr>
                </table>
            </div>
            <div class="lf-col">
                <div class="lf-section-title">Method of Contact</div>
                ${mocHtml}
            </div>
        </div>

        <div class="lf-two-col">
            <div class="lf-col">
                <div class="lf-section-title">Rescue Equipment</div>
                ${equipHtml}
            </div>
            <div class="lf-col">
                <div class="lf-section-title">Location of Equipment</div>
                ${locHtml}
            </div>
        </div>

        <div class="lf-section-title">Critical Rescue Factors</div>
        <table class="lf-info-table">
            <tr><td class="label" style="width:25%;">Anchor Point (if Applicable)</td><td><div class="lf-text-block">${esc(plan.anchorPoint || '—')}</div></td></tr>
            <tr><td class="label">Landing Area</td><td><div class="lf-text-block">${esc(plan.landingArea || '—')}</div></td></tr>
            <tr><td class="label">Rescue Obstructions / Hazards</td><td><div class="lf-text-block">${esc(plan.obstructionsHazards || '—')}</div></td></tr>
        </table>

        <div class="lf-section-title">Verification Checklist</div>
        <table class="lf-checklist-table">
            <thead><tr><th style="width:60%;">Question</th><th style="width:70px;">Answer</th><th>Comment</th></tr></thead>
            <tbody>${checklistRows}</tbody>
        </table>

        <div class="lf-two-col">
            <div class="lf-col">
                <div class="lf-section-title">Pre-Work Tasks (Rescue Prep)</div>
                ${preWorkHtml}
            </div>
            <div class="lf-col">
                <div class="lf-section-title">Response Procedure</div>
                ${responseHtml}
            </div>
        </div>

        <div class="lf-section-title">Submitter Acknowledgment</div>
        <table class="lf-submitter-table">
            <tr>
                <td class="label" style="width:14%;">Submitted By</td>
                <td style="width:28%;">${esc(plan.submitterName || plan.submittedByName || '—')}</td>
                <td class="label" style="width:14%;">Submitted At</td>
                <td style="width:22%;">${esc(plan.submittedAt ? fmtDateTime(plan.submittedAt) : '—')}</td>
                <td class="label" style="width:10%;">Signature</td>
                <td>${sigCell}</td>
            </tr>
        </table>
    `;
}

/**
 * Faithful Confined Space Entry Permit — mirrors
 * templates/admin/confined_space_entry_permit/pdf.html.twig.
 * The IDB record is nested (stepOne/stepTwo); we flatten it to the fields the
 * server template uses. Returns an array of TWO portrait pages.
 */
function renderCsepPages(rec) {
    if (!rec) return [];
    const s1 = rec.stepOne || {};
    const s2 = rec.stepTwo || {};
    // Flatten (prefer nested step data, fall back to a flat record if present).
    const p = {
        jobCode: s1.jobCode ?? rec.jobCode ?? '',
        jobName: s1.jobName ?? rec.jobName ?? '',
        location: s1.location ?? rec.location ?? '',
        dateTime: s1.dateTime ?? rec.dateTime ?? '',
        entrySupervisorName: s1.entrySupervisorName ?? rec.entrySupervisorName ?? '',
        entrySupervisorPhone: s1.entrySupervisorPhone ?? rec.entrySupervisorPhone ?? '',
        preEntryAnswers: s1.preEntryAnswers ?? rec.preEntryAnswers ?? {},
        preEntryNote: s1.preEntryNote ?? rec.preEntryNote ?? '',
        project: s2.project ?? rec.project ?? '',
        permitType: s2.permitType ?? rec.permitType ?? 'permit',
        confinedSpaceLocation: s2.confinedSpaceLocation ?? rec.confinedSpaceLocation ?? '',
        locationOfConfinedSpace: s2.locationOfConfinedSpace ?? rec.locationOfConfinedSpace ?? '',
        entryDateTime: s2.entryDateTime ?? rec.entryDateTime ?? '',
        confinedSpaceTypes: s2.confinedSpaceTypes ?? rec.confinedSpaceTypes ?? [],
        confinedSpaceTypeOther: s2.confinedSpaceTypeOther ?? rec.confinedSpaceTypeOther ?? '',
        workDescription: s2.workDescription ?? rec.workDescription ?? '',
        energizedSources: s2.energizedSources ?? rec.energizedSources ?? [],
        verifications: s2.verifications ?? rec.verifications ?? {},
        specialRequirements: s2.specialRequirements ?? rec.specialRequirements ?? {},
        otherRequirements: s2.otherRequirements ?? rec.otherRequirements ?? [],
        respiratorType: s2.respiratorType ?? rec.respiratorType ?? '',
        attendants: s2.attendants ?? rec.attendants ?? [],
        airMonitoringEquipment: s2.airMonitoringEquipment ?? rec.airMonitoringEquipment ?? {},
        atmosphereCheckedBy: s2.atmosphereCheckedBy ?? rec.atmosphereCheckedBy ?? '',
        airMonitoringReadings: s2.airMonitoringReadings ?? rec.airMonitoringReadings ?? {},
        otherContaminants: s2.otherContaminants ?? rec.otherContaminants ?? [],
        emergencyContact: s2.emergencyContact ?? rec.emergencyContact ?? '911',
        emergencyContactOther: s2.emergencyContactOther ?? rec.emergencyContactOther ?? '',
        entrySupervisorSignature: s2.entrySupervisorSignature ?? rec.entrySupervisorSignature ?? '',
        entrySupervisorSignatureDate: s2.entrySupervisorSignatureDate ?? rec.entrySupervisorSignatureDate ?? '',
        submittedAt: rec.submittedAt ?? rec.meta?.submittedAt ?? '',
        submittedByName: rec.submittedByName ?? rec.submitterName ?? (rec.meta && rec.meta.submittedBy) ?? '',
        status: (rec.status || 'draft'),
    };

    const ans = (key) => {
        const a = p.preEntryAnswers[key];
        if (a === 'yes') return '<span class="lf-csep-yes">YES</span>';
        if (a === 'no') return '<span class="lf-csep-no">NO</span>';
        if (a === 'na') return '<span class="lf-csep-na">N/A</span>';
        return '—';
    };

    // ── Page 1: Job info + Pre-Entry Checklist (two-column split) ──
    const half = Math.ceil(PRE_ENTRY_QUESTIONS.length / 2);
    let checklistRows = '';
    for (let i = 0; i < half; i++) {
        const left = PRE_ENTRY_QUESTIONS[i];
        const right = PRE_ENTRY_QUESTIONS[i + half];
        checklistRows += `<tr>
            <td style="text-align:center;">${left ? left.id : ''}</td>
            <td>${left ? esc(left.text) : ''}</td>
            <td style="text-align:center;">${left ? ans(left.id) : ''}</td>
            <td style="text-align:center;">${right ? right.id : ''}</td>
            <td>${right ? esc(right.text) : ''}</td>
            <td style="text-align:center;">${right ? ans(right.id) : ''}</td>
        </tr>`;
    }

    const page1 = `
        <div class="lf-doc-title">CONFINED SPACE ENTRY PERMIT</div>
        <div class="lf-csep-subtitle">Attachment 7A - Pre-Entry Checklist &amp; Attachment 7B - Entry Permit</div>

        <div class="lf-csep-section">Job Information</div>
        <table class="lf-two"><tr>
            <td><table class="lf-csep-info">
                <tr><td class="lbl">Job Code:</td><td>${esc(p.jobCode || '—')}</td></tr>
                <tr><td class="lbl">Job Name:</td><td>${esc(p.jobName || '—')}</td></tr>
                <tr><td class="lbl">Location:</td><td>${esc(p.location || '—')}</td></tr>
            </table></td>
            <td><table class="lf-csep-info">
                <tr><td class="lbl">Date/Time:</td><td>${esc(p.dateTime ? fmtDateTime(p.dateTime) : '—')}</td></tr>
                <tr><td class="lbl">Entry Supervisor:</td><td>${esc(p.entrySupervisorName || '—')}</td></tr>
                <tr><td class="lbl">Phone:</td><td>${esc(p.entrySupervisorPhone || '—')}</td></tr>
            </table></td>
        </tr></table>

        <div class="lf-csep-section">Pre-Entry Checklist (Attachment 7A)</div>
        <table class="lf-csep-grid">
            <thead><tr>
                <th style="width:4%;">#</th><th style="width:36%;">Question</th><th style="width:10%;">Answer</th>
                <th style="width:4%;">#</th><th style="width:36%;">Question</th><th style="width:10%;">Answer</th>
            </tr></thead>
            <tbody>${checklistRows}</tbody>
        </table>
        ${p.preEntryNote ? `<p style="font-size:8pt;margin-top:4px;"><strong>Notes:</strong> ${esc(p.preEntryNote)}</p>` : ''}
    `;

    // ── Page 2: Entry details, hazards, verifications, special reqs, air monitoring, authorization ──
    const types = (p.confinedSpaceTypes || []).map(t => {
        const found = CONFINED_SPACE_TYPES.find(x => x.id === t);
        return found ? found.label : t;
    });
    let typesText = types.length ? esc(types.join(', ')) : '—';
    if ((p.confinedSpaceTypes || []).includes('other') && p.confinedSpaceTypeOther) {
        typesText += ` (${esc(p.confinedSpaceTypeOther)})`;
    }

    let hazardsHtml = '';
    if ((p.energizedSources || []).length) {
        const rows = p.energizedSources.map(src => {
            const hz = (src.hazards || []).map(h => {
                const t = h.name || h.value || h;
                return esc(String(t).split(':')[0]);
            }).join(', ');
            return `<tr><td>${esc(src.value || src.name || '—')}</td><td>${hz || '—'}</td></tr>`;
        }).join('');
        hazardsHtml = `<div class="lf-csep-section">Hazards &amp; Energized Sources</div>
            <table class="lf-csep-grid"><thead><tr><th style="width:35%;">Energized Source</th><th style="width:65%;">Associated Hazards</th></tr></thead>
            <tbody>${rows}</tbody></table>`;
    }

    const verRows = VERIFICATION_ITEMS.map(item => {
        const v = p.verifications[item.id] || {};
        const sig = v.signature ? `<img src="${v.signature}" alt="sig" style="max-width:100px;max-height:30px;">` : '—';
        return `<tr><td>${esc(item.label)}</td><td style="text-align:center;">${v.date ? esc(fmtDate(v.date)) : '—'}</td><td style="text-align:center;">${esc(v.signedBy || '—')}</td><td style="text-align:center;">${sig}</td></tr>`;
    }).join('');

    const cbox = (on) => `<span class="lf-csep-cbox${on ? ' on' : ''}"></span>`;
    const reqHalf = Math.ceil(SPECIAL_REQUIREMENTS.length / 2);
    const reqRow = (req) => {
        if (!req) return '';
        const d = p.specialRequirements[req.id] || { required: false, verified: false };
        const label = req.id === 'respiratorsRequired' && p.respiratorType
            ? `Respirators Req'd: <strong>${esc(p.respiratorType)}</strong>`
            : esc(req.label);
        return `<tr><td>${label}</td><td style="text-align:center;">${cbox(d.required)}</td><td style="text-align:center;">${cbox(d.verified)}</td></tr>`;
    };
    let reqLeft = '';
    for (let i = 0; i < reqHalf; i++) reqLeft += reqRow(SPECIAL_REQUIREMENTS[i]);
    let reqRight = '';
    for (let i = reqHalf; i < SPECIAL_REQUIREMENTS.length; i++) reqRight += reqRow(SPECIAL_REQUIREMENTS[i]);
    (p.otherRequirements || []).forEach(o => {
        reqRight += `<tr style="background:#fffde7;"><td>${esc(o.name || 'Other')}</td><td style="text-align:center;">${cbox(o.required)}</td><td style="text-align:center;">${cbox(o.verified)}</td></tr>`;
    });

    const attendantsText = (p.attendants || []).length
        ? esc(p.attendants.map(a => a.name || 'Unknown').join(', '))
        : '—';

    const air = p.airMonitoringEquipment || {};
    let airRows = AIR_CONTAMINANTS.map(c => {
        const r = p.airMonitoringReadings[c.id] || {};
        const cell = (chk) => {
            const v = (r[chk] && r[chk].value) || '—';
            const t = (r[chk] && r[chk].time) ? `<br><small style="color:#666;">${esc(fmtDateTime(r[chk].time))}</small>` : '';
            return `${esc(v)}${t}`;
        };
        return `<tr><td style="text-align:left;">${esc(c.label)}</td><td>${esc(c.pel)}</td><td>${cell('check1')}</td><td>${cell('check2')}</td><td>${cell('check3')}</td></tr>`;
    }).join('');
    (p.otherContaminants || []).forEach(o => {
        const cell = (chk) => {
            const v = (o[chk] && o[chk].value) || '—';
            const t = (o[chk] && o[chk].time) ? `<br><small style="color:#666;">${esc(fmtDateTime(o[chk].time))}</small>` : '';
            return `${esc(v)}${t}`;
        };
        airRows += `<tr><td style="text-align:left;">${esc(o.name || 'Other')}</td><td>${esc(o.pel || '—')}</td><td>${cell('check1')}</td><td>${cell('check2')}</td><td>${cell('check3')}</td></tr>`;
    });

    const page2 = `
        <div class="lf-csep-section">Entry Permit Details (Attachment 7B)</div>
        <table class="lf-two"><tr>
            <td><table class="lf-csep-info">
                <tr><td class="lbl">Project:</td><td>${esc(p.project || p.jobName || '—')}</td></tr>
                <tr><td class="lbl">Permit Type:</td><td>${p.permitType === 'non-permit' ? 'Non-Permit' : 'Permit Required'}</td></tr>
                <tr><td class="lbl">Entry Supervisor:</td><td>${esc(p.entrySupervisorName || '—')}</td></tr>
            </table></td>
            <td><table class="lf-csep-info">
                <tr><td class="lbl">Location:</td><td>${esc(p.confinedSpaceLocation || p.location || '—')}</td></tr>
                <tr><td class="lbl">Loc. of Confined Space:</td><td>${esc(p.locationOfConfinedSpace || '—')}</td></tr>
                <tr><td class="lbl">Entry Date/Time:</td><td>${esc(p.entryDateTime ? fmtDateTime(p.entryDateTime) : '—')}</td></tr>
                <tr><td class="lbl">Space Types:</td><td>${typesText}</td></tr>
                <tr><td class="lbl">Work Description:</td><td>${esc(p.workDescription || '—')}</td></tr>
            </table></td>
        </tr></table>

        ${hazardsHtml}

        <div class="lf-csep-section">Verifications</div>
        <table class="lf-csep-grid"><thead><tr><th style="width:40%;">Item</th><th style="width:15%;">Date</th><th style="width:20%;">Signed By</th><th style="width:25%;">Signature</th></tr></thead>
        <tbody>${verRows}</tbody></table>

        <div class="lf-csep-section">Special Requirements (Completed &amp; Reviewed Prior to Entry)</div>
        <table class="lf-two"><tr>
            <td><table class="lf-csep-grid"><thead><tr><th style="width:70%;">Requirement</th><th style="width:15%;">Req</th><th style="width:15%;">Ver</th></tr></thead><tbody>${reqLeft}</tbody></table></td>
            <td><table class="lf-csep-grid"><thead><tr><th style="width:70%;">Requirement</th><th style="width:15%;">Req</th><th style="width:15%;">Ver</th></tr></thead><tbody>${reqRight}</tbody></table></td>
        </tr></table>
        <p style="font-size:8pt;margin-top:4px;"><strong>Attendant(s) Name(s):</strong> ${attendantsText}</p>

        <div class="lf-csep-section">Air Monitoring</div>
        <table class="lf-two"><tr>
            <td><table class="lf-csep-info">
                <tr><td class="lbl">Make/Model:</td><td>${esc(air.make || '—')} / ${esc(air.model || '—')}</td></tr>
                <tr><td class="lbl">Equipment ID:</td><td>${esc(air.equipmentId || '—')}</td></tr>
            </table></td>
            <td><table class="lf-csep-info">
                <tr><td class="lbl">Calibration Date:</td><td>${esc(air.calibrationDate ? fmtDate(air.calibrationDate) : '—')}</td></tr>
                <tr><td class="lbl">Calibrated By:</td><td>${esc(air.calibratedBy || '—')}</td></tr>
            </table></td>
        </tr></table>
        <p style="font-size:8pt;margin:4px 0;"><strong>Atmosphere Checked By:</strong> ${esc(p.atmosphereCheckedBy || '—')}</p>
        <table class="lf-csep-grid"><thead><tr><th style="text-align:left;width:25%;">Contaminant</th><th style="width:20%;">PEL</th><th>Check 1</th><th>Check 2</th><th>Check 3</th></tr></thead>
        <tbody>${airRows}</tbody></table>

        <div class="lf-csep-section">Authorization</div>
        <table class="lf-two"><tr>
            <td><div class="lf-csep-sigbox"><h6>Entry Supervisor Authorization</h6>
                <table>
                    <tr><td class="lbl" style="font-weight:bold;width:35%;">Name:</td><td>${esc(p.entrySupervisorName || '—')}</td></tr>
                    <tr><td class="lbl" style="font-weight:bold;">Emergency Contact:</td><td>${esc(p.emergencyContact || '911')}${p.emergencyContactOther ? ` (${esc(p.emergencyContactOther)})` : ''}</td></tr>
                    <tr><td class="lbl" style="font-weight:bold;">Date:</td><td>${esc(p.entrySupervisorSignatureDate ? fmtDateTime(p.entrySupervisorSignatureDate) : '—')}</td></tr>
                </table>
                ${p.entrySupervisorSignature ? `<img src="${p.entrySupervisorSignature}" alt="Entry Supervisor Signature" class="lf-csep-sigimg">` : ''}
            </div></td>
            <td><div class="lf-csep-sigbox"><h6>Submission Information</h6>
                <table>
                    <tr><td class="lbl" style="font-weight:bold;width:35%;">Submitted By:</td><td>${esc(p.submittedByName || '—')}</td></tr>
                    <tr><td class="lbl" style="font-weight:bold;">Submitted At:</td><td>${esc(p.submittedAt ? fmtDateTime(p.submittedAt) : '—')}</td></tr>
                    <tr><td class="lbl" style="font-weight:bold;">Status:</td><td>${esc(String(p.status).toUpperCase())}</td></tr>
                </table>
            </div></td>
        </tr></table>
    `;

    return [page1, page2];
}

/**
 * Faithful Lock-Tag-Try Permit — mirrors
 * templates/admin/lock_tag_try_permit/pdf.html.twig.
 * IDB record uses nested groups (header / controlMeasures / verifyZeroEnergy /
 * lotoChecklist / emergencyArrangements / permitDuration / signatures); we map to
 * the flat fields the server template uses. Returns one portrait page.
 */
function renderLttPages(rec) {
    if (!rec) return [];
    const header = rec.header || {};
    const cm = rec.controlMeasures || {};
    const vz = rec.verifyZeroEnergy || {};
    const dur = rec.permitDuration || {};
    const emerg = rec.emergencyArrangements || {};
    const sigs = rec.signatures || {};
    const p = {
        workCarriedOutBy: header.workCarriedOutBy ?? rec.workCarriedOutBy ?? '',
        jobCode: header.jobCode ?? rec.jobCode ?? '',
        jobName: header.jobName ?? rec.jobName ?? '',
        location: header.location ?? rec.location ?? '',
        hasCategory3Multimeter: header.hasCategory3Multimeter ?? rec.hasCategory3Multimeter ?? false,
        equipment: header.equipment ?? rec.equipment ?? [],
        descriptionOfWork: header.descriptionOfWork ?? rec.descriptionOfWork ?? '',
        arcFlashPpeCategory: cm.arcFlashPpeCategory ?? rec.arcFlashPpeCategory ?? '',
        noLabelCategory4Required: cm.noLabelCategory4Required ?? rec.noLabelCategory4Required ?? false,
        energySelections: rec.energySelections ?? {},
        hazardNotes: rec.hazardNotes ?? '',
        startDateTime: dur.startDateTime ?? rec.startDateTime ?? null,
        endDateTime: dur.endDateTime ?? rec.endDateTime ?? null,
        controlQ1: cm.controlQ1 ?? rec.controlMeasuresMethod ?? rec.controlMeasuresNotes ?? '',
        controlQ2: cm.controlQ2 ?? '',
        controlQ3: cm.controlQ3 ?? '',
        verifyQ1: vz.verifyQ1 ?? rec.verifyZeroEnergyNotes ?? '',
        verifyQ2: vz.verifyQ2 ?? '',
        lotoAnswers: (rec.lotoChecklist && rec.lotoChecklist.answers) ?? rec.lotoChecklistAnswers ?? {},
        emergencySelections: emerg.selections ?? rec.emergencySelections ?? [],
        nearestEmergencyLocation: emerg.nearestEmergencyLocation ?? rec.nearestEmergencyLocation ?? '',
        emergencyContact: emerg.emergencyContact ?? rec.emergencyContact ?? '',
        lockoutLog: rec.lockoutLog ?? [],
        workers: rec.workers ?? [],
        submittedByName: (rec.meta && rec.meta.submittedByName) ?? rec.submittedByName ?? rec.submitterName ?? '',
        submittedAt: rec.submittedAt ?? (rec.meta && rec.meta.submittedAt) ?? '',
    };

    const qText = (id) => {
        const it = LOTO_CHECKLIST_ITEMS.find(x => x.id === id);
        return it ? it.text : id;
    };
    const ynaCell = (id) => {
        const a = p.lotoAnswers[id];
        return `<td style="text-align:center;">${a === 'yes' ? '<span class="lf-ltt-yes">X</span>' : ''}</td>
                <td style="text-align:center;">${a === 'no' ? '<span class="lf-ltt-no">X</span>' : ''}</td>
                <td style="text-align:center;">${a === 'na' ? '<span class="lf-ltt-na">X</span>' : ''}</td>`;
    };
    const qRow = (id) => `<tr><td>${esc(qText(id))}</td>${ynaCell(id)}</tr>`;
    const procHeader = (label) => `<tr><td colspan="4" class="lf-ltt-proc">${esc(label)}</td></tr>`;
    const emergRow = (id, label) => {
        const on = (p.emergencySelections || []).includes(id);
        return `<tr><td>${esc(label)}</td>
            <td style="text-align:center;">${on ? '<span class="lf-ltt-yes">X</span>' : ''}</td>
            <td></td>
            <td style="text-align:center;">${on ? '' : '<span class="lf-ltt-na">X</span>'}</td></tr>`;
    };

    const cbox = (on) => `<span class="lf-cbox${on ? ' on' : ''}"></span>`;

    // Energy identification grid (3 per row)
    let energyGrid = '';
    for (let i = 0; i < LTT_ENERGY_TYPES.length; i += 3) {
        const row = LTT_ENERGY_TYPES.slice(i, i + 3);
        energyGrid += `<tr>${row.map(et =>
            `<td style="padding:1px 2px;font-size:7pt;">${cbox(!!p.energySelections[et.id])} ${esc(et.label)}</td>`
        ).join('')}</tr>`;
    }

    const equipExtra = (p.equipment || []).map(it => esc(it.name || it)).join(', ');

    // Lockout log / workers fallback
    let logHtml = '';
    if ((p.lockoutLog || []).length) {
        const rows = p.lockoutLog.map((e, i) => `<tr>
            <td style="text-align:center;">${i + 1}</td>
            <td>${esc(e.lockTagNumber || '---')}</td>
            <td>${esc(e.name || '---')}</td>
            <td>${esc(e.phone || '---')}</td>
            <td style="text-align:center;">${e.dateApplied ? esc(fmtDate(e.dateApplied)) : '---'}</td>
            <td style="text-align:center;">${e.releasedDate ? esc(fmtDate(e.releasedDate)) : '---'}</td>
            <td style="text-align:center;">${e.releasedSignature ? `<img src="${e.releasedSignature}" style="max-width:60px;max-height:20px;" alt="">` : '---'}</td>
        </tr>`).join('');
        logHtml = `<div class="lf-ltt-section">Lockout Log</div>
            <table class="lf-ltt-log"><thead><tr>
                <th style="width:5%;">#</th><th style="width:12%;">Lock/Tag #</th><th style="width:25%;">Name</th>
                <th style="width:15%;">Phone</th><th style="width:15%;">Date Applied</th><th style="width:15%;">Released Date</th><th style="width:13%;">Released Sign</th>
            </tr></thead><tbody>${rows}</tbody></table>`;
    } else if ((p.workers || []).length) {
        const rows = p.workers.map((w, i) => `<tr>
            <td style="text-align:center;">${i + 1}</td>
            <td>${esc(w.FullName || w.name || '---')}</td>
            <td style="text-align:center;">${w.signatureIn ? `<img src="${w.signatureIn}" style="max-width:60px;max-height:20px;" alt="">` : '---'}</td>
            <td style="text-align:center;">${w.signatureOut ? `<img src="${w.signatureOut}" style="max-width:60px;max-height:20px;" alt="">` : '---'}</td>
        </tr>`).join('');
        logHtml = `<div class="lf-ltt-section">Workers</div>
            <table class="lf-ltt-log"><thead><tr><th style="width:5%;">#</th><th style="width:35%;">Name</th><th style="width:30%;">Sign In</th><th style="width:30%;">Sign Out</th></tr></thead><tbody>${rows}</tbody></table>`;
    }

    const initiator = sigs.checklistInitiator || sigs.permitIssuer || {};
    const verification = sigs.completionVerification || sigs.handBack || {};

    const page = `
        <div class="lf-doc-title">Lock-Tag-Try Checklist (&lt;600V)</div>

        <table class="lf-ltt-info" style="font-size:7pt;">
            <tr>
                <td class="lbl" style="width:14%;white-space:nowrap;">WORK BY:</td><td style="width:31%;">${esc(p.workCarriedOutBy || '---')}</td>
                <td class="lbl" style="width:8%;white-space:nowrap;">JOB #:</td><td style="width:10%;">${esc(p.jobCode || '---')}</td>
                <td class="lbl" style="width:10%;white-space:nowrap;">LOCATION:</td><td style="width:27%;">${esc(p.location || p.jobName || '---')}</td>
            </tr>
        </table>

        <table class="lf-ltt-info">
            <tr>
                <td class="lbl" style="width:20%;">EQUIPMENT TO BE USED:</td>
                <td style="width:80%;">
                    ${cbox(p.hasCategory3Multimeter)} Category III 600V Multimeter
                    ${equipExtra ? ` &nbsp;|&nbsp; ${equipExtra}` : ''}
                    &nbsp;|&nbsp; <strong>Arc Flash PPE:</strong> ${esc(p.arcFlashPpeCategory || '---')}
                    &nbsp; ${cbox(p.noLabelCategory4Required)} NO LABEL, Cat 4 (40 kcal) PPE REQD
                </td>
            </tr>
        </table>

        <div class="lf-ltt-section">Description of Work</div>
        <table class="lf-ltt-info"><tr><td>${esc(p.descriptionOfWork || '---')}</td></tr></table>

        <table class="lf-two"><tr>
            <td style="width:60%;">
                <div class="lf-ltt-section" style="margin-top:0;">Energy Identification</div>
                <div class="lf-box2">
                    <table style="width:100%;border-collapse:collapse;">${energyGrid}</table>
                    ${p.hazardNotes ? `<p style="margin:4px 0 0 0;font-size:7pt;white-space:pre-wrap;">${esc(p.hazardNotes)}</p>` : ''}
                </div>
            </td>
            <td style="width:40%;">
                <div class="lf-ltt-section" style="margin-top:0;">Task Duration</div>
                <div class="lf-box2">
                    <table class="lf-dur">
                        <tr><th>DATE</th><td>${p.startDateTime ? esc(fmtDate(p.startDateTime)) : '---'}</td><th>DATE</th><td>${p.endDateTime ? esc(fmtDate(p.endDateTime)) : '---'}</td></tr>
                        <tr><th>TIME</th><td>${p.startDateTime ? esc(fmtDateTime(p.startDateTime).split(' ').slice(1).join(' ')) : '---'}</td><th>TIME</th><td>${p.endDateTime ? esc(fmtDateTime(p.endDateTime).split(' ').slice(1).join(' ')) : '---'}</td></tr>
                    </table>
                </div>
            </td>
        </tr></table>

        <table class="lf-two"><tr>
            <td style="width:60%;">
                <div class="lf-ltt-section" style="margin-top:0;">Control Measures</div>
                <div class="lf-box2">
                    <p style="font-size:7pt;font-weight:bold;margin:2px 0 1px 0;">How are you controlling the energy above?</p>
                    <p style="font-size:8pt;margin:0 0 4px 0;white-space:pre-wrap;">${esc(p.controlQ1 || '---')}</p>
                    <p style="font-size:7pt;font-weight:bold;margin:4px 0 1px 0;">How are you releasing stored energy?</p>
                    <p style="font-size:8pt;margin:0 0 4px 0;white-space:pre-wrap;">${esc(p.controlQ2 || '---')}</p>
                    <p style="font-size:7pt;font-weight:bold;margin:4px 0 1px 0;">EX: LOCKING BREAKER, VALVE, AIR-GAPPING, etc.</p>
                    <p style="font-size:8pt;margin:0;white-space:pre-wrap;">${esc(p.controlQ3 || '---')}</p>
                </div>
                <div class="lf-ltt-section">Verify Zero Energy</div>
                <div class="lf-box2">
                    <p style="font-size:7pt;font-weight:bold;margin:2px 0 1px 0;">How are you verifying zero energy?</p>
                    <p style="font-size:8pt;margin:0 0 4px 0;white-space:pre-wrap;">${esc(p.verifyQ1 || '---')}</p>
                    <p style="font-size:7pt;font-weight:bold;margin:4px 0 1px 0;">Is there any stored energy?</p>
                    <p style="font-size:8pt;margin:0;white-space:pre-wrap;">${esc(p.verifyQ2 || '---')}</p>
                </div>
            </td>
            <td style="width:40%;">
                <table class="lf-ltt-loto">
                    <thead><tr><th style="width:70%;text-align:left;">LOTO Checklist (If "NO", do not proceed)</th><th style="width:10%;">YES</th><th style="width:10%;">NO</th><th style="width:10%;">N/A</th></tr></thead>
                    <tbody>
                        ${qRow('taskSpecificJsa')}
                        ${qRow('workersTrainedLtt')}
                        ${procHeader('LOTO Procedure')}
                        ${qRow('identifyEnergySources')}
                        ${qRow('isolateEnergySources')}
                        ${qRow('applyLockoutDevices')}
                        ${qRow('verifyZeroEnergy')}
                        ${qRow('verifyMultimeter')}
                        ${procHeader('Carry Out Task')}
                        ${qRow('removeLockoutDevices')}
                        ${procHeader('Emergency Arrangements')}
                        ${emergRow('rescuePlan', 'Rescue plan')}
                        ${emergRow('fireExtinguisher', 'Fire Extinguisher')}
                        ${emergRow('firstAiderOnSite', 'First aider on site')}
                        <tr><td><strong>Nearest emergency service location and contact:</strong></td><td colspan="3">${esc(p.nearestEmergencyLocation || '---')}${p.emergencyContact ? ` / ${esc(p.emergencyContact)}` : ''}</td></tr>
                    </tbody>
                </table>
            </td>
        </tr></table>

        <div class="lf-ltt-sigbox">
            <h6>CHECKLIST INITIATOR</h6>
            <p class="desc">I confirm that I have verified the above information &amp; associated Risk Assessment for this work &amp; ensured that all necessary precautions have been taken.</p>
            <table><tr>
                <td style="font-weight:bold;width:10%;">NAME:</td><td style="width:25%;">${esc(initiator.name || '---')}</td>
                <td style="font-weight:bold;width:10%;">DATE:</td><td style="width:20%;">${initiator.date ? esc(fmtDateTime(initiator.date)) : '---'}</td>
                <td style="font-weight:bold;width:10%;">SIGN:</td><td style="width:25%;">${initiator.signature ? `<img src="${initiator.signature}" class="lf-ltt-sigimg" alt="">` : '---'}</td>
            </tr></table>
        </div>

        <div class="lf-ltt-sigbox">
            <h6>COMPLETION VERIFICATION</h6>
            <p class="desc">I confirm the work has been completed.</p>
            <table>
                <tr>
                    <td style="font-weight:bold;width:10%;">NAME:</td><td style="width:25%;">${esc(verification.name || '---')}</td>
                    <td style="font-weight:bold;width:10%;">DATE:</td><td style="width:20%;">${verification.date ? esc(fmtDateTime(verification.date)) : '---'}</td>
                    <td style="font-weight:bold;width:10%;">SIGN:</td><td style="width:25%;">${verification.signature ? `<img src="${verification.signature}" class="lf-ltt-sigimg" alt="">` : '---'}</td>
                </tr>
                ${verification.completionStatus ? `<tr><td style="font-weight:bold;">STATUS:</td><td colspan="5">${verification.completionStatus === 'completed' ? 'Completed' : 'Partially Completed'}</td></tr>` : ''}
            </table>
        </div>

        ${logHtml}

        <table class="lf-ltt-info" style="margin-top:10px;">
            <tr>
                <td class="lbl" style="width:20%;">Submitted By:</td><td style="width:30%;">${esc(p.submittedByName || '---')}</td>
                <td class="lbl" style="width:20%;">Submitted At:</td><td style="width:30%;">${p.submittedAt ? esc(fmtDateTime(p.submittedAt)) : '---'}</td>
            </tr>
        </table>
    `;

    return [page];
}

/**
 * Registry of linked-form renderers, keyed by type. Each entry: { title, render }.
 * `render(data, opts)` returns the inner HTML of ONE page (string) OR multiple
 * pages (string[]). The wrapper handles page numbering, orientation and styling.
 *
 * To add a new form type: implement renderXxx(data) and register it here.
 */
export const FORM_RENDERERS = {
    farp: { title: 'Fall Arrest Rescue Plan', render: renderFarpInner, wrapperClass: '' },
    csep: { title: 'Confined Space Entry Permit', render: renderCsepPages, wrapperClass: 'lf-csep' },
    ltt: { title: 'Lockout/Tagout Permit', render: renderLttPages, wrapperClass: 'lf-ltt' },
};

/**
 * Build full portrait pages for a list of linked forms and continue the JHA page
 * numbering. Returns { html, nextPageNum }.
 *
 * @param {Array<{type: string, data: object}>} forms
 * @param {number} startPageNum - the next page number to use (JHA last page + 1)
 */
// Full-page rendering of a hand-filled form photographed (or any attached photo
// of a form). Big and readable — the image fills the page, NOT a tiny thumbnail.
function renderPhotoInner(title, dataUrl) {
    return `<div style="display:flex;flex-direction:column;height:100%;">
        <div class="lf-doc-title">${esc(title || 'Attached Document')}</div>
        <div class="lf-subtitle">Attached photo / hand-completed form</div>
        <div style="flex:1 1 auto;display:flex;align-items:center;justify-content:center;">
            <img src="${dataUrl}" alt="${esc(title || '')}" style="max-width:100%;max-height:840px;width:auto;height:auto;object-fit:contain;border:1px solid #ccc;" />
        </div>
    </div>`;
}

/**
 * Render full portrait pages for linked forms AND form photos, continuing the JHA
 * page numbering. Each item is one of:
 *   { type: 'farp'|'csep'|'ltt', data }   → digital form rendered pixel-faithfully
 *   { type: 'photo', title, dataUrl }      → one full readable page for a photo
 * Returns { html, nextPageNum }.
 */
export function renderLinkedFormPages(forms, startPageNum, opts = {}) {
    let pageNum = startPageNum;
    let html = '';

    const emitPage = (inner, wrapperClass, withHeader) => {
        if (!inner) return;
        html += `<div class="page-break"></div>`;
        // Mirrors the server PDF: the company HEADER appears only on the FIRST page
        // of a form, the FOOTER repeats on EVERY page. Layout: flex column at
        // letter-portrait aspect (760px wide → ~984px tall); content flexes to fill,
        // pushing the footer to the page bottom (html2canvas renders flexbox reliably).
        html += `<div class="page-${pageNum} lf-page ${wrapperClass}" data-orientation="portrait" `
            + `style="width:760px;min-height:984px;background:#fff;padding:26px 30px;display:flex;flex-direction:column;">`
            + `${withHeader ? companyHeaderHtml() : ''}`
            + `<div class="lf-content" style="flex:1 1 auto;display:flex;flex-direction:column;">${inner}</div>`
            + `${companyFooterHtml()}`
            + `</div>`;
        pageNum += 1;
    };

    (forms || []).forEach((item) => {
        if (!item) return;
        // Photo of a form (hand-filled & photographed) → its own readable full page.
        if (item.type === 'photo') {
            if (!item.dataUrl) return;
            emitPage(renderPhotoInner(item.title, item.dataUrl), 'lf-photo', true);
            return;
        }
        // Digital form rendered from data.
        const renderer = FORM_RENDERERS[item.type];
        if (!renderer || !item.data) return;
        const result = renderer.render(item.data, opts);
        const pages = Array.isArray(result) ? result : [result];
        const wrapperClass = renderer.wrapperClass || '';
        pages.forEach((inner, idx) => emitPage(inner, wrapperClass, idx === 0));
    });

    return { html, nextPageNum: pageNum };
}

export default renderLinkedFormPages;
